/**
 * Machine membership calls: create a join token, revoke a machine. Against the hub these are
 * POST /api/join-tokens and DELETE /api/machines/:id. In demo mode they are simulated so the
 * whole flow can be seen, including a new machine joining.
 */
import { JOIN_TOKEN_TTL_MS, joinCommands, joinRunIn } from '@orgus/protocol';
import type { Id, JoinTokenResponse, JoinTokenTrustFields, Machine } from '@orgus/protocol';
import { apiDelete, apiPost } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';

/**
 * The hub offers one command per shell, the hub address and where to run it; older hubs send only
 * `command`. COMPAT-3: the hub key fingerprint the command pins, or the note saying why it does not.
 */
export type JoinToken = JoinTokenResponse & JoinTokenTrustFields;

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));
let mockJoinTimer: ReturnType<typeof setTimeout> | null = null;

function randomToken(): string {
  const a = new Uint8Array(18);
  crypto.getRandomValues(a);
  return `fdj_${Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

export async function createJoinToken(): Promise<JoinToken> {
  const st = useOrgus.getState();
  if (st.mode !== 'mock') return apiPost<JoinToken>('/api/join-tokens');
  await delay(500);
  const token = randomToken();
  const hub = `${window.location.protocol}//${window.location.hostname}:4600`;
  // Demo: a new machine answers the command a few seconds later.
  if (mockJoinTimer) clearTimeout(mockJoinTimer);
  mockJoinTimer = setTimeout(() => {
    const s = useOrgus.getState();
    if (s.mode !== 'mock') return;
    const now = Date.now();
    const n = Object.keys(s.machines).length + 1;
    const m: Machine = {
      id: `m-new-${now.toString(36)}`,
      name: `build-0${n}`,
      os: 'linux',
      arch: 'x64',
      hostname: `build-0${n}.lan`,
      cpus: 16,
      memTotal: 64 * 1024 ** 3,
      memFree: 51 * 1024 ** 3,
      load: 0.06,
      agentVersion: '0.1.0',
      connectedAt: now,
      lastSeen: now,
      online: true,
      labels: [],
    };
    s.applyPatch({ at: now, upserts: { machines: [m] }, removes: {} });
    s.applyEvent({ id: `f-join-${now}`, at: now, kind: 'machine.online', title: `${m.name} connected`, machineId: m.id, projectId: null });
  }, 7000);
  const fingerprint = 'SHA256:q3J7mWfK2cVbN8xT1pLs9RdYhE4uGz6oA0iCkMnBvXw';
  const commands = joinCommands(token, hub, fingerprint);
  return {
    token,
    expiresAt: Date.now() + JOIN_TOKEN_TTL_MS,
    command: commands.posix,
    commands,
    hubUrl: hub,
    runIn: joinRunIn(JOIN_TOKEN_TTL_MS),
    hubKeyFingerprint: fingerprint,
  };
}

export function cancelMockJoin(): void {
  if (mockJoinTimer) clearTimeout(mockJoinTimer);
  mockJoinTimer = null;
}

/** The command for each shell; an older hub that sent only `command` gets npx.cmd on Windows, which PowerShell runs even where scripts are blocked. */
export function commandsFor(t: JoinToken): { windows: string; posix: string } {
  const posix = t.commands?.posix ?? t.command;
  const windows = t.commands?.windows ?? t.command.replace(/^npx\s/, 'npx.cmd ');
  return { windows, posix };
}

export async function revokeMachine(id: Id): Promise<void> {
  const st = useOrgus.getState();
  if (st.mode !== 'mock') {
    await apiDelete(`/api/machines/${encodeURIComponent(id)}`);
    return;
  }
  await delay(600);
  const s = useOrgus.getState();
  const now = Date.now();
  s.applyPatch({
    at: now,
    upserts: {},
    removes: {
      machines: [id],
      checkouts: Object.values(s.checkouts).filter((c) => c.machineId === id).map((c) => c.id),
      sessions: Object.values(s.sessions).filter((x) => x.machineId === id).map((x) => x.id),
      jobs: Object.values(s.jobs).filter((j) => j.machineId === id).map((j) => j.id),
      pipelines: Object.entries(s.pipelines).filter(([, p]) => p.machineId === id).map(([k]) => k),
      suggestions: Object.entries(s.suggestions).filter(([, x]) => x.machineId === id).map(([k]) => k),
    },
  });
}
